import { Spin } from "antd";
import { BoxForm, Button, Input, Select } from "components";
import usePayment from "hooks/usePayment";
import React, { useEffect, useMemo } from 'react'
import * as S from "../styles"
import { useFormContext, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useGetDebitsManager } from "../hooks/useGetDebitsManager";
import { GetAllDebitsRequest } from "../../providers/dto/get-debits.dto";
import { useGetDetrans } from "views/Partner/providers/get-detrans.provider";
import { EnumPublicDepartaments } from "../../types";

interface BoxFormPublicDepartmentProps {
  isDisabled: boolean;
  setVariables: (variables: Pick<GetAllDebitsRequest, "document" | "municipal_registration" | "property_registration_number" | "id_establishment" | "public_department">) => void;
  idEstablishment: string;
  publicDepartment: EnumPublicDepartaments;
}

const BoxFormPublicDepartment = ({ isDisabled, setVariables, idEstablishment, publicDepartment }: BoxFormPublicDepartmentProps) => {
  const { control, handleSubmit, formState: { errors }, setValue } = useFormContext()
  const { dataPaymentBillet } = usePayment()
  const [searchParams] = useSearchParams()
  const document_query = searchParams.get("document")

  const { data: partners } = useGetDetrans({
    id_establishment: idEstablishment,
    type: "PUBLIC_DEPARTMENT",
  })

  const partnerData = useMemo(() => {
    return partners?.data.find((item) => item.city === publicDepartment);
  }, [partners, publicDepartment])

  const selectedFieldKey: string = useWatch({
    control,
    name: "public_department_search_field",
  }) || partnerData?.fields?.[0]?.input_key?.toLowerCase() || ""

  const selectedField = useMemo(() => {
    return partnerData?.fields?.find(
      (field) => field.input_key.toLowerCase() === selectedFieldKey
    );
  }, [partnerData, selectedFieldKey])

  const selectedValue = useWatch({
    control,
    name: selectedFieldKey || "document",
  })

  const { mutate: getDebitsPublicDepartment, isLoading: isLoadingGetDebts } = useGetDebitsManager({ license_plate: "", isPublicDepartment: true, setVariablesRequest: setVariables })

  const fetchDebtsPublicDepartment = async (data: Record<string, string>) => {
    const value = data[selectedFieldKey];

    if (!value) return;

    getDebitsPublicDepartment({
      id_establishment: idEstablishment,
      public_department: publicDepartment,
      ...(selectedFieldKey === "document" && { document: value.replace(/\D/g, "") }),
      ...(selectedFieldKey === "municipal_registration" && { municipal_registration: value }),
      ...(selectedFieldKey === "property_registration_number" && { property_registration_number: value }),
    });
  };

  useEffect(() => {
    if (!partnerData?.fields?.length) return;

    setValue("public_department_search_field", partnerData.fields[0].input_key.toLowerCase());
  }, [partnerData])

  useEffect(() => {
    if (document_query) {
      setValue("public_department_search_field", "document")
      setValue("document", document_query)
      getDebitsPublicDepartment({
        id_establishment: idEstablishment,
        public_department: publicDepartment,
        document: document_query.replace(/\D/g, ""),
      })
    }
  }, [])

  const mask = useMemo(() => {
    if (selectedFieldKey !== "document") return undefined;
    return selectedValue?.length <= 14 ? "999.999.999-999" : "99.999.999/9999-99"
  }, [selectedFieldKey, selectedValue])

  return (
    <BoxForm hide={!dataPaymentBillet?.debits.length}>
      <Spin spinning={false}>
        <S.WrapperBoxForm>
          <S.TitleForm>Informe os dados para consultarmos.</S.TitleForm>

          <S.WrapperImage disabled={!partnerData?.image_src}>
            <S.ImgDetran src={partnerData?.image_src} />
          </S.WrapperImage>

          <S.WrapperInput key={"public_department_search_field"}>
            <Select
              control={control}
              name="public_department_search_field"
              error={errors["public_department_search_field"]?.message as string}
            >
              {partnerData?.fields?.map((field) => (
                <S.Option key={field.input_key} value={field.input_key.toLowerCase()}>
                  {field.label}
                </S.Option>
              ))}
            </Select>
          </S.WrapperInput>

          {selectedField && (
            <S.WrapperInput key={selectedField.input_key}>
              <Input
                type="text"
                type_input="normal"
                placeholder={selectedField.label}
                name={selectedField.input_key.toLowerCase()}
                control={control}
                required={selectedField.required}
                mask={mask}
                error={
                  errors[selectedField.input_key.toLowerCase()]?.message as string
                }
              />
            </S.WrapperInput>
          )}

          <S.WrapperButton>
            <S.Button>
              <Button
                title="Pesquisar"
                type_button="primary"
                loading={isLoadingGetDebts}
                disabled={isLoadingGetDebts || isDisabled || !!Object.keys(errors).length}
                action={handleSubmit(fetchDebtsPublicDepartment)}
              />
            </S.Button>
          </S.WrapperButton>
        </S.WrapperBoxForm>
      </Spin>
    </BoxForm>
  )
}

export default BoxFormPublicDepartment
